/* packages/volto-castle-theme/src/components/Blocks/HPLatestPublished/HPLatestPublished.tsx */
import React from 'react';
import config from '@plone/volto/registry';

import b1 from '../../../static/img/b1.png';
import b2 from '../../../static/img/b2.png';
import b3 from '../../../static/img/b3.png';

type Card = {
  image: string;
  category: string;
  title: string;
  date: string;
  excerpt: string;
  href: string;
};

type PloneSearchItem = {
  '@id'?: string;
  title?: string;
  description?: string;
  effective?: string;
  created?: string;
  modified?: string;

  // Common image-ish fields (varies by type/behaviors)
  image?: any;
  preview_image?: any;
  leadImage?: any;

  // Sometimes present on objects / fullobjects
  image_scales?: any;

  // Plone 6 default "Preview image" on News Items often comes as a link object:
  // preview_image_link: { @id, image_scales: { image: [ { scales: { teaser: { download }}}]}}
  preview_image_link?: any;
};

const FALLBACK_IMAGES = [b1, b2, b3];

const FALLBACK_CARDS: Card[] = [
  {
    image: b1,
    category: 'NEWS',
    title: 'Latest updates coming soon',
    date: '',
    excerpt: 'Publish “News Item” content under /news to populate this section automatically.',
    href: '/news',
  },
  {
    image: b2,
    category: 'NEWS',
    title: 'Add News Items in Plone',
    date: '',
    excerpt: 'Create a News Item and publish it. This section will always show the latest three.',
    href: '/news',
  },
  {
    image: b3,
    category: 'NEWS',
    title: 'Keep your homepage fresh',
    date: '',
    excerpt: 'No code changes needed—just add or edit content in the CMS.',
    href: '/news',
  },
];

const EXCERPT_MAX_CHARS = 100;

const truncateText = (text: string, maxChars: number) => {
  const s = (text || '').trim();
  if (!s) return '';
  if (s.length <= maxChars) return s;

  const cut = s.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');
  const safeCut = lastSpace > Math.min(60, maxChars - 1) ? cut.slice(0, lastSpace) : cut;

  return `${safeCut}…`;
};

/**
 * Prefer Volto config, but support the common dev-proxy setup.
 * - If apiPath is "http://localhost:3000", we actually want "http://localhost:3000/++api++"
 * - If apiPath is already "/++api++" or ".../++api++", use it as-is.
 * - If apiPath points directly to backend "...:8080/Plone", use it as-is.
 */
const getApiBase = () => {
  const fromRegistry = (config as any)?.settings?.apiPath;
  const fromEnv = typeof window !== 'undefined' ? (window as any)?.env?.apiPath : undefined;

  let apiPath = String(fromRegistry || fromEnv || '').replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;

    // If runtime is giving frontend origin, use the dev proxy path.
    if (apiPath === origin) {
      apiPath = `${origin}/++api++`;
    }

    // If it's clearly the frontend origin but missing the proxy
    if (apiPath.startsWith(origin) && !apiPath.includes('/++api++') && apiPath.includes(':3000')) {
      apiPath = `${origin}/++api++`;
    }
  }

  return apiPath.replace(/\/$/, '');
};

const getBackendBaseFromRegistry = () => {
  // If your volto.config.js sets apiPath to backend (e.g. http://localhost:8080/Plone),
  // we can strip it from returned absolute URLs to make app-relative paths.
  const v = (config as any)?.settings?.apiPath;
  return String(v || '').replace(/\/$/, '');
};

const flattenToAppURL = (url: string) => {
  if (!url) return '';
  const apiBase = getApiBase();

  // If we are using /++api++ proxy, strip it from returned URLs if it appears
  const proxyIdx = apiBase.indexOf('/++api++');
  const proxyPrefix = proxyIdx >= 0 ? apiBase.slice(0, proxyIdx) + '/++api++' : '';

  if (proxyPrefix && url.startsWith(proxyPrefix)) {
    const out = url.slice(proxyPrefix.length);
    return out || '/';
  }

  // If returned url starts with backend api base (e.g. http://localhost:8080/Plone), strip it
  const backendBase = getBackendBaseFromRegistry();
  if (backendBase && url.startsWith(backendBase)) {
    const out = url.slice(backendBase.length);
    return out || '/';
  }

  if (url.startsWith('/')) return url;
  return url;
};

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const joinURL = (base: string, path: string) => {
  if (!base) return path || '';
  if (!path) return base || '';
  return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
};

const pickPreviewImage = (it: PloneSearchItem): string => {
  //  0) Plone 6 "Preview image" on News Items is commonly: preview_image_link (Image object)
  const pil = (it as any)?.preview_image_link;
  const pilId: string | undefined = pil?.['@id'];

  // download paths inside preview_image_link are often relative like "@@images/image-600-....png"
  const pilScale: string | undefined =
    pil?.image_scales?.image?.[0]?.scales?.teaser?.download ||
    pil?.image_scales?.image?.[0]?.scales?.preview?.download ||
    pil?.image_scales?.image?.[0]?.scales?.large?.download ||
    pil?.image_scales?.image?.[0]?.download;

  if (typeof pilId === 'string' && pilId && typeof pilScale === 'string' && pilScale) {
    return flattenToAppURL(joinURL(pilId, pilScale));
  }

  // 1) image_scales on the item itself
  const scales = it?.image_scales;
  const s1 =
    (scales as any)?.image?.scales?.teaser?.download ||
    (scales as any)?.image?.scales?.preview?.download ||
    (scales as any)?.image?.scales?.large?.download;
  if (typeof s1 === 'string' && s1) return flattenToAppURL(s1);

  // 2) common image fields with scales/download
  const img = it?.image || it?.preview_image || it?.leadImage;
  const s2 =
    (img as any)?.scales?.teaser?.download ||
    (img as any)?.scales?.preview?.download ||
    (img as any)?.scales?.large?.download ||
    (img as any)?.download;
  if (typeof s2 === 'string' && s2) return flattenToAppURL(s2);

  // 3) image field with @id
  const s3 = (img as any)?.['@id'];
  if (typeof s3 === 'string' && s3) return flattenToAppURL(s3);

  return '';
};

const mapItemsToCards = (items: PloneSearchItem[]): Card[] => {
  const mapped: Card[] = items.slice(0, 3).map((it, idx) => {
    const preview = pickPreviewImage(it);
    const href = flattenToAppURL(it['@id'] || '');

    return {
      image: preview || FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length],
      category: 'NEWS',
      title: it.title || 'Untitled',
      date: formatDate(it.effective || it.created || it.modified),
      excerpt: it.description || '',
      href: href || '/news',
    };
  });

  return mapped.length ? mapped : FALLBACK_CARDS;
};

/**
 * Build two possible "path" values for Plone @search:
 * - "/news" (what you say works in your setup)
 * - "<siteId>/news" derived from apiBase pathname (e.g. "/Plone/news") (what many setups require)
 *
 * We try BOTH, in order, and use the first that returns items.
 */
const getCandidateSearchPaths = () => {
  const paths: string[] = [];

  // Always try /news first (your request)
  paths.push('/news');

  // If apiBase is backend with site id in pathname (e.g. http://localhost:8080/Plone),
  // also try "/Plone/news".
  const apiBase = getApiBase();
  try {
    const u = new URL(apiBase);
    const siteRoot = (u.pathname || '').replace(/\/$/, ''); // "/Plone" or ""
    if (siteRoot && siteRoot !== '/') {
      const physical = `${siteRoot}/news`;
      if (!paths.includes(physical)) paths.push(physical);
    }
  } catch {
    // ignore
  }

  // de-dupe
  return Array.from(new Set(paths));
};

const fetchNewsItems = async (signal: AbortSignal) => {
  const apiBase = getApiBase();
  if (!apiBase) return { items: [] as PloneSearchItem[], items_total: 0 };

  const candidatePaths = getCandidateSearchPaths();

  for (const path of candidatePaths) {
    const params = new URLSearchParams();
    params.set('portal_type', 'News Item');
    params.set('path', path);
    params.set('sort_on', 'effective');
    params.set('sort_order', 'descending');
    params.set('b_size', '3');
    params.set('fullobjects', '1');

    const url = `${apiBase}/@search?${params.toString()}`;

    const res = await fetch(url, {
      signal,
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) continue;

    const data = await res.json();
    const itemsTotal = Number(data?.items_total ?? 0);
    const items: PloneSearchItem[] = Array.isArray(data?.items) ? data.items : [];

    // Use first candidate that returns actual items
    if (itemsTotal > 0 && items.length > 0) {
      return { items, items_total: itemsTotal };
    }
  }

  return { items: [] as PloneSearchItem[], items_total: 0 };
};

const HPLatestPublished: React.FC = () => {
  const [cards, setCards] = React.useState<Card[]>(FALLBACK_CARDS);
  const [debug, setDebug] = React.useState<{ loading: boolean; itemsTotal?: number; err?: string }>(
    { loading: true },
  );

  React.useEffect(() => {
    const apiBase = getApiBase();
    if (!apiBase) {
      setDebug({ loading: false, err: 'No apiPath configured' });
      setCards(FALLBACK_CARDS);
      return;
    }

    const ctrl = new AbortController();

    const run = async () => {
      try {
        const { items, items_total } = await fetchNewsItems(ctrl.signal);

        setDebug({ loading: false, itemsTotal: items_total });

        if (!items.length) {
          setCards(FALLBACK_CARDS);
          return;
        }

        setCards(mapItemsToCards(items));
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setDebug({ loading: false, err: 'Fetch failed' });
        setCards(FALLBACK_CARDS);
      }
    };

    run();
    return () => ctrl.abort();
  }, []);

  return (
    <section className="hp-latest hp-fullbleed" aria-label="Latest published">
      <div className="hp-latest__container">
        <header className="hp-latest__heading">
          <h2 className="hp-latest__title">LATEST PUBLISHED</h2>
          <div className="hp-latest__titleRule" />
        </header>

        {/* TEMP DEBUG (uncomment if needed) */}
        {/* <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
          apiBase: {getApiBase() || '(none)'} | loading: {String(debug.loading)} | items_total:{' '}
          {debug.itemsTotal ?? '-'} | err: {debug.err ?? 'none'} | paths:{' '}
          {getCandidateSearchPaths().join(', ')}
        </div> */}

        <div className="hp-latest__grid">
          {cards.map((c, idx) => (
            <article className="hp-latest__card" key={`${c.title}-${idx}`}>
              <div className="hp-latest__media" aria-hidden="true">
                <img className="hp-latest__img" src={c.image} alt="" />
              </div>

              <div className="hp-latest__body">
                <div className="hp-latest__category">{c.category}</div>

                <h3 className="hp-latest__cardTitle">{c.title}</h3>

                {c.date ? <div className="hp-latest__date">{c.date}</div> : null}

                <p className="hp-latest__excerpt">{truncateText(c.excerpt, EXCERPT_MAX_CHARS)}</p>

                <a className="hp-latest__more" href={c.href} aria-label={`Read more: ${c.title}`}>
                  READ MORE <span aria-hidden="true">→</span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HPLatestPublished;